import React, { useEffect, useState } from "react";
import { Share, X, Plus } from "lucide-react";

/**
 * iOS Safari-only install hint.
 *
 * iOS Safari does not fire `beforeinstallprompt` and offers no JS install API
 * (only AddToHomeScreen via the Share sheet). So InstallPrompt.jsx silently
 * never shows on iOS. This component fills that gap with a one-time,
 * dismissible visual cue pointing at the Share button.
 */
const DISMISS_KEY = "cor_ios_install_dismissed";
const DISMISS_DAYS = 30;
const APPEAR_DELAY_MS = 4500; // Don't nag immediately.

function detectIOSSafari() {
  if (typeof navigator === "undefined" || typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  if (!isIOS) return false;
  // iPadOS 13+ reports as Mac. Catch that too.
  const isIPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  if (!isIOS && !isIPadOS) return false;
  // Exclude in-app browsers (FB, Instagram, etc.) — they can't add to home screen.
  if (/FBAN|FBAV|Instagram|Line|Twitter/.test(ua)) return false;
  // Chrome on iOS uses CriOS — also can't AddToHomeScreen
  if (/CriOS|FxiOS|EdgiOS/.test(ua)) return false;
  return true;
}

export default function IOSInstallHint() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!detectIOSSafari()) return;
    // Already installed?
    if (window.navigator.standalone === true) return;
    // Recently dismissed?
    try {
      const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (ts && (Date.now() - ts) < DISMISS_DAYS * 86_400_000) return;
    } catch { /* ignore */ }

    const id = setTimeout(() => setVisible(true), APPEAR_DELAY_MS);
    return () => clearTimeout(id);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="ios-install-hint"
      role="dialog"
      aria-label="Install Correlate on iOS"
      // Bottom-anchored above the mobile tab bar (safe-area aware).
      className="md:hidden fixed left-3 right-3 bottom-[calc(64px+env(safe-area-inset-bottom)+12px)] z-[60] paper p-4 shadow-xl animate-in fade-in slide-in-from-bottom-4 duration-300"
    >
      <button
        data-testid="ios-install-dismiss"
        onClick={dismiss}
        className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
        aria-label="Dismiss install hint"
      >
        <X size={14} />
      </button>

      <div className="flex items-start gap-3">
        <div className="serif italic text-3xl text-[hsl(var(--primary))] leading-none mt-0.5 select-none">C</div>
        <div className="flex-1 min-w-0">
          <div className="serif text-lg text-foreground leading-tight">
            Install Cor·relate
          </div>
          <p className="text-[13px] text-muted-foreground mt-1.5 leading-relaxed">
            Tap the{" "}
            <Share size={13} className="inline -mt-0.5 text-[hsl(var(--primary))]" strokeWidth={2} />
            {" "}<span className="serif italic">Share</span> button, then{" "}
            <span className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent))] border border-[hsl(var(--accent))/0.3] px-1.5 py-0.5 align-baseline">
              <Plus size={10} strokeWidth={2.5} />
              Add to Home Screen
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
